import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import type { PhaseTestResult } from "./phase-request-mappers";

type ApiPhase = ResponseBody<
  paths["/{tenantId}/cycles/{id}/phases"]["get"]
>["data"][number];

export type PgtPersistenceStatus = "reported" | "not_performed" | "pending";

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Build the currently supported, cycle-linked PGT acknowledgement record.
 * This intentionally stores only a patient-entered summary in the existing
 * test-result contract. Embryo-level report/provenance linkage remains a
 * separate backend feature and must not be inferred here.
 */
export function buildPgtTestResult(
  data: Record<string, string | number | boolean | null>,
  status: PgtPersistenceStatus,
  recordedDate: string,
  existingPhase?: Pick<ApiPhase, "testResults"> | null,
): PhaseTestResult {
  const existing = existingPhase?.testResults?.find(
    (result) => result.name?.toLowerCase() === "pgt",
  );
  const euploidCount = numericValue(data.euploidCount);
  const pgtNotes =
    typeof data.pgtNotes === "string" && data.pgtNotes.trim() !== ""
      ? data.pgtNotes.trim()
      : (existing?.notes ?? null);

  return {
    ...(existing?.id ? { id: existing.id } : {}),
    name: "PGT",
    type: "genetics",
    bloodTestCategory: null,
    urineTestCategory: null,
    geneticsTestCategory: "other",
    imagingTestCategory: null,
    samplingTestCategory: null,
    clinicalTestCategory: null,
    semenFluidTestCategory: null,
    biopsyTestCategory: null,
    otherCategory: "preimplantation_genetic_testing",
    patientValue:
      status === "not_performed"
        ? "not_performed"
        : status === "pending"
          ? "pending"
          : euploidCount == null
            ? "reported"
            : String(euploidCount),
    doctorValue: null,
    valueType:
      status === "reported" && euploidCount != null ? "numeric" : "string",
    unit: euploidCount == null ? null : "embryos",
    date: recordedDate,
    notes: pgtNotes,
    patientAssessmentNormal: null,
    clinicalAssessment: "unknown",
    clinicalAssessmentDetails: null,
    loincCode: null,
    attachmentId:
      typeof data.pgtResultUploadId === "string" &&
      data.pgtResultUploadId.trim() !== ""
        ? data.pgtResultUploadId
        : (existing?.attachment?.uploadId ?? null),
  };
}

function hasPgtDecision(
  value: unknown,
): value is { name?: unknown; patientValue?: unknown } {
  return (
    typeof value === "object" &&
    value !== null &&
    "name" in value &&
    "patientValue" in value
  );
}

/**
 * Confirm that the phase read-back contains the PGT decision just written.
 * The treatment hub must not leave a completed badge backed only by an
 * optimistic client update when the API accepted the request but did not
 * return the durable test result.
 */
export function pgtTestResultMatches(
  phase: { testResults?: unknown[] } | null | undefined,
  expected: PhaseTestResult,
): boolean {
  if (!hasPgtDecision(expected) || typeof expected.name !== "string") {
    return false;
  }
  const expectedName = expected.name;
  const persisted = phase?.testResults?.find((result) => {
    if (!hasPgtDecision(result) || typeof result.name !== "string") {
      return false;
    }
    return result.name.toLowerCase() === expectedName.toLowerCase();
  });
  if (!hasPgtDecision(persisted)) return false;
  return persisted?.patientValue === expected.patientValue;
}
